import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  related_id: number | null;
  created_at: string;
}

export function useNotifications(userId: string | undefined) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [newToast, setNewToast] = useState<Notification | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      const allNotifs = (data || []) as Notification[];

      // Get read notification IDs
      const { data: reads, error: readsError } = await supabase
        .from("notification_reads")
        .select("notification_id")
        .eq("user_id", userId);

      if (readsError) throw readsError;

      const readIds = new Set((reads || []).map((r: { notification_id: number }) => r.notification_id));
      const unread = allNotifs.filter((n) => !readIds.has(n.id));

      setNotifications(allNotifs);
      setUnreadCount(unread.length);
    } catch {
      // silently fail - notifications are non-critical
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Supabase Realtime subscription
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel("notifications-changes")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications" },
        (payload) => {
          const newNotif = payload.new as Notification;
          setNotifications((prev) => [newNotif, ...prev]);
          setUnreadCount((prev) => prev + 1);
          setNewToast(newNotif);
          setTimeout(() => setNewToast(null), 5000);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const markAsRead = useCallback(
    async (notificationId: number) => {
      if (!userId) return;

      try {
        await supabase.from("notification_reads").upsert(
          { user_id: userId, notification_id: notificationId, read_at: new Date().toISOString() },
          { onConflict: "user_id,notification_id" },
        );

        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // silently fail
      }
    },
    [userId],
  );

  const markAllAsRead = useCallback(async () => {
    if (!userId || notifications.length === 0) return;

    try {
      const { data: reads } = await supabase
        .from("notification_reads")
        .select("notification_id")
        .eq("user_id", userId);

      const readIds = new Set((reads || []).map((r: { notification_id: number }) => r.notification_id));
      const unreadIds = notifications.filter((n) => !readIds.has(n.id)).map((n) => n.id);

      if (unreadIds.length === 0) return;

      const inserts = unreadIds.map((nid) => ({
        user_id: userId,
        notification_id: nid,
        read_at: new Date().toISOString(),
      }));

      await supabase.from("notification_reads").upsert(inserts, { onConflict: "user_id,notification_id" });
      setUnreadCount(0);
    } catch {
      // silently fail
    }
  }, [userId, notifications]);

  const dismissToast = useCallback(() => {
    setNewToast(null);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    showDropdown,
    setShowDropdown,
    newToast,
    dismissToast,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
}